import { useMemo, useState } from 'react';
import {
  Button,
  IconButton,
  Loader,
  Message,
  SelectPicker,
  Stack,
  Toggle,
  toaster,
} from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';

import {
  useActualsEmailAlerts,
  useCreateActualsEmailAlert,
  useUpdateActualsEmailAlert,
  useDeleteActualsEmailAlert,
} from '../hooks/useQueries';

const FREQUENCY_OPTIONS = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
];

const WEEKDAY_OPTIONS = [
  { label: 'Sunday', value: 0 },
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => {
  const period = h < 12 ? 'AM' : 'PM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return { label: `${hour12}:00 ${period}`, value: h };
});

// 1..28 keeps every month valid (no clamping surprises for the user).
const DAY_OF_MONTH_OPTIONS = Array.from({ length: 28 }, (_, i) => {
  const d = i + 1;
  const suffix = d % 10 === 1 && d !== 11 ? 'st' : d % 10 === 2 && d !== 12 ? 'nd' : d % 10 === 3 && d !== 13 ? 'rd' : 'th';
  return { label: `${d}${suffix}`, value: d };
});

const MONTHS_BACK_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const n = i + 1;
  return { label: `Last ${n} month${n === 1 ? '' : 's'}`, value: n };
});

const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const labelForHour = (h) => HOUR_OPTIONS.find((o) => o.value === h)?.label || `${h}:00`;
const labelForWeekday = (d) => WEEKDAY_OPTIONS.find((o) => o.value === d)?.label || '—';
const labelForDom = (d) => DAY_OF_MONTH_OPTIONS.find((o) => o.value === d)?.label || `${d}`;

const describeAlert = (a) => {
  const when = labelForHour(Number(a.hourLocal));
  const span = `last ${a.monthsBack} month${Number(a.monthsBack) === 1 ? '' : 's'}`;
  if (a.frequency === 'daily') return `Daily at ${when} • ${span}`;
  if (a.frequency === 'weekly') return `Weekly on ${labelForWeekday(Number(a.dayOfWeek))} at ${when} • ${span}`;
  if (a.frequency === 'monthly') return `Monthly on the ${labelForDom(Number(a.dayOfMonth))} at ${when} • ${span}`;
  return `${a.frequency} at ${when} • ${span}`;
};

// `taskId`/`taskName` scope the modal to a single discipline; without them it
// manages the task order's alerts (every discipline in one email).
export default function ActualsEmailAlertsModal({ open, onClose, projectId, taskOrderId, taskId = null, taskName = null }) {
  const timezone = useMemo(() => getBrowserTimeZone(), []);

  const scopedTaskId = Number.isInteger(taskId) ? taskId : null;
  const isDisciplineScope = scopedTaskId !== null;
  const scopeNoun = isDisciplineScope ? 'discipline' : 'task order';

  const enabled = open && Number.isInteger(projectId) && Number.isInteger(taskOrderId);
  const { data, isLoading, isError } = useActualsEmailAlerts(projectId, taskOrderId, scopedTaskId, { enabled });
  const alerts = useMemo(() => (Array.isArray(data?.alerts) ? data.alerts : []), [data]);

  const createAlert = useCreateActualsEmailAlert();
  const updateAlert = useUpdateActualsEmailAlert();
  const deleteAlert = useDeleteActualsEmailAlert();

  // New-alert form state.
  const [frequency, setFrequency] = useState('weekly');
  const [hourLocal, setHourLocal] = useState(8);
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [monthsBack, setMonthsBack] = useState(3);

  const pushError = (msg) =>
    toaster.push(
      <Message showIcon type="error" closable>
        {msg}
      </Message>,
      { placement: 'topEnd', duration: 6000 }
    );

  const pushSuccess = (msg) =>
    toaster.push(
      <Message showIcon type="success" closable>
        {msg}
      </Message>,
      { placement: 'topEnd', duration: 3000 }
    );

  const handleCreate = async () => {
    try {
      await createAlert.mutateAsync({
        projectId,
        taskOrderId,
        ...(isDisciplineScope ? { taskId: scopedTaskId } : {}),
        frequency,
        hourLocal,
        timezone,
        monthsBack,
        ...(frequency === 'weekly' ? { dayOfWeek } : {}),
        ...(frequency === 'monthly' ? { dayOfMonth } : {}),
      });
      pushSuccess('Email alert created.');
    } catch (e) {
      pushError(e?.message?.replace(/^HTTP \d+:\s*/, '') || 'Failed to create alert.');
    }
  };

  const handleToggle = async (alert, isActive) => {
    try {
      await updateAlert.mutateAsync({ id: alert.id, isActive });
    } catch (e) {
      pushError(e?.message?.replace(/^HTTP \d+:\s*/, '') || 'Failed to update alert.');
    }
  };

  const handleDelete = async (alert) => {
    try {
      await deleteAlert.mutateAsync(alert.id);
      pushSuccess('Email alert removed.');
    } catch (e) {
      pushError(e?.message?.replace(/^HTTP \d+:\s*/, '') || 'Failed to remove alert.');
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="sm" overflow>
      <Modal.Header>
        <Modal.Title>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-clock" aria-hidden="true" />
            Email Alerts
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '60vh' }}>
        {isDisciplineScope ? (
          <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 6 }}>
            <i className="bi bi-diagram-3" aria-hidden="true" style={{ marginRight: 6 }} />
            {taskName || `Task ${scopedTaskId}`}
          </div>
        ) : null}
        <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
          {isDisciplineScope
            ? 'Get a recurring email with this discipline\u2019s projected vs. actual hours and an attached spreadsheet \u2014 only this task, not the rest of the task order.'
            : 'Get a recurring email with this task order\u2019s projected vs. actual hours and an attached spreadsheet.'}{' '}
          Alerts are sent to your account email. Times are in your timezone
          {timezone ? ` (${timezone})` : ''}.
        </p>

        {/* Existing alerts */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Your alerts</div>
          {isLoading ? (
            <Loader content="Loading…" />
          ) : isError ? (
            <Message type="error" showIcon>
              Failed to load your alerts.
            </Message>
          ) : alerts.length === 0 ? (
            <div style={{ fontSize: 13, color: '#999' }}>You have no alerts set up for this {scopeNoun} yet.</div>
          ) : (
            <Stack direction="column" alignItems="stretch" spacing={8}>
              {alerts.map((a) => (
                <div
                  key={a.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    padding: '8px 10px',
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    background: a.isActive ? '#fff' : '#fafafa',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: a.isActive ? '#1a1a1a' : '#999' }}>
                      {describeAlert(a)}
                    </div>
                    <div style={{ fontSize: 11, color: '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.recipientEmail}
                    </div>
                  </div>
                  <Stack spacing={8} alignItems="center">
                    <Toggle
                      size="sm"
                      checked={a.isActive}
                      onChange={(checked) => handleToggle(a, checked)}
                      title={a.isActive ? 'Active' : 'Paused'}
                    />
                    <IconButton
                      size="xs"
                      appearance="subtle"
                      icon={<i className="bi bi-trash" aria-hidden="true" />}
                      aria-label="Delete alert"
                      title="Delete alert"
                      onClick={() => handleDelete(a)}
                    />
                  </Stack>
                </div>
              ))}
            </Stack>
          )}
        </div>

        {/* New alert form */}
        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 14 }}>
          <div style={{ fontWeight: 800, marginBottom: 10 }}>Add an alert</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Frequency</div>
              <SelectPicker
                block
                cleanable={false}
                searchable={false}
                data={FREQUENCY_OPTIONS}
                value={frequency}
                onChange={(v) => setFrequency(v || 'weekly')}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Time of day</div>
              <SelectPicker
                block
                cleanable={false}
                searchable={false}
                data={HOUR_OPTIONS}
                value={hourLocal}
                onChange={(v) => setHourLocal(Number.isInteger(v) ? v : 8)}
              />
            </div>

            {frequency === 'weekly' ? (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Day of week</div>
                <SelectPicker
                  block
                  cleanable={false}
                  searchable={false}
                  data={WEEKDAY_OPTIONS}
                  value={dayOfWeek}
                  onChange={(v) => setDayOfWeek(Number.isInteger(v) ? v : 1)}
                />
              </div>
            ) : null}

            {frequency === 'monthly' ? (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Day of month</div>
                <SelectPicker
                  block
                  cleanable={false}
                  searchable={false}
                  data={DAY_OF_MONTH_OPTIONS}
                  value={dayOfMonth}
                  onChange={(v) => setDayOfMonth(Number.isInteger(v) ? v : 1)}
                />
              </div>
            ) : null}

            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Data to include</div>
              <SelectPicker
                block
                cleanable={false}
                searchable={false}
                data={MONTHS_BACK_OPTIONS}
                value={monthsBack}
                onChange={(v) => setMonthsBack(Number.isInteger(v) ? v : 3)}
              />
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <Button appearance="primary" onClick={handleCreate} loading={createAlert.isPending}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <i className="bi bi-plus-lg" aria-hidden="true" />
                Add alert
              </span>
            </Button>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <NsiButton onClick={onClose}>
          Close
        </NsiButton>
      </Modal.Footer>
    </Modal>
  );
}
